import React, { useRef } from "react";
import { NavLink, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  Header,
  StarlinkObstructionMap,
  roboticSystems,
  formatDateTime,
  formatMbps,
  formatPercent,
  orDash,
  splitDuration,
  useStarlinkStatus,
} from "@shared/ui";

import "./StarlinkSettingsPage.css";

const StarlinkSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { droneId } = useParams();
  const [searchParams] = useSearchParams();
  const isWidget = searchParams.get("widget") === "1";
  const {
    status: data,
    obstructionMap,
    isMapLoading,
    error,
    isConnected,
    refresh,
    loadObstructionMap,
  } = useStarlinkStatus(droneId);

  const dish = data?.status ?? null;
  const location = data?.location ?? null;
  const yesNo = (value: boolean) =>
    value ? t("starlinkSettingsPage.common.yes") : t("starlinkSettingsPage.common.no");
  const degrees = (value: number) => `${value.toFixed(1)}°`;
  const obstructionMapRef = useRef<HTMLDivElement>(null);

  const showObstructionMap = () => {
    obstructionMapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    loadObstructionMap();
  };

  const renderBadge = () => {
    if (!data) {
      return (
        <span className="starlink-badge starlink-badge-muted">
          {t("starlinkSettingsPage.header.waiting")}
        </span>
      );
    }
    return data.isDishReachable ? (
      <span className="starlink-badge starlink-badge-success">
        ● {t("starlinkSettingsPage.header.connected")}
      </span>
    ) : (
      <span className="starlink-badge starlink-badge-danger">
        ● {t("starlinkSettingsPage.header.disconnected")}
      </span>
    );
  };

  return (
    <div className="container">
      {isWidget ? (
        <div className="header-wrapper">
          <header>
            <span className="logo">
              <img src={roboticSystems} width="80" alt="RoboticSystems" />
            </span>
          </header>
        </div>
      ) : (
        <>
          <Header />
          <NavLink className="btn btn-text text-left" to={"/dashboard/"}>
            {t("settingsPage.backButton")}
          </NavLink>
        </>
      )}

      <div className={isWidget ? "starlink-page" : "starlink-page mt-20"}>
        <div className="starlink-card starlink-header">
          <div className="starlink-header-top">
            <div className="starlink-title">
              <div className="starlink-logo">
                <img
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAACXBIWXMAAAsTAAALEwEAmpwYAAADwklEQVR4nO2aS0hVQRjHfzfTSqlwURa2t8euVm1CKFpkRtBrEVREUataChF4qSSJJIhKKUhqFUbRYxFEkCCBFIERWRJk9gATM5KM6HXjwFwaPmbOPffe8/Ke+4fZ3Dkz8/3/8833zeNCGWWUESLSQCMJJp8BHpJg8hlVGpNMPpMkL0gbyAfhBc447cSc/A3gbABeoI/THmfylUAdMOWzF3SLsdrjSj6LDp+9oAZ4ERcR0jnIO1gM/NK+cdoUixXANzG2H/36Tj4FnDcExPY8g6jp+z159Bsr8hmLsW4ZxEauOwoR0h7Jd4nvOlUxuW0u8jY3N8WDwJdCnzbYkzzIp1SRIvR6JG8jqMeDUOJAs2bMJFCbg3yX+l3/RoqQb0kb4kFoQTAFPNeMOeJCTJLPYobBdYsVIVTs0gz5BFQD54SBlxRRiZTh20JLZPm/EhjRDHnmceZNS6TYEhkOWwwKY+ZjIUANMC6MuRjizEcugMkLepUwYZGPXAAHLcIgZ58w1+NusCQEsIkwLwTyrcQIaWHcaMDkI90HePWERMy8hNeDTaFlWlywtgQsQuTXYFF4wmAcY0AVsBO4DrwD/ijj3gN3gCEfA16lunuIjQibFemgA55O0iaCafcZKE6GQNw2wyYRHgMbwhRgn+buYac6R4Cbljb96sImFBwE/oY487bZ/21o/whYN109oTWPmXeO3gtVWpyyCNEcR08Y8mHm5aVLnXiLzNVnpCLcMuwTBg03zF7JE3WaXABM5CGAc53m5Y1hWpB30JPnEvjJf9hEmDbktxUQ6HQBbCKEQr5Ja+hcbxXi+mMGghM5YsJwnmeHziDIO1ivNT6OP67fo4RxC4zXLP2ZRHCu3OcE5fYrheESn8UgFS4Gj6nloMMmwg4Xm0wijKoXqFq/13xtDrfsFwPVWwzOzroJUoRhS7rTcQD4YBBiUj3I+hrw3midLRV1J8RguwscQxdho8c2s4D9wGuX2OBLtL+qdXhM1C0TszcAzCxChLYC2lWoJTMQVKrbqnX6EZgt6i+LgU8RDVLqyNvnd56vBr5qBA+JemdtvzXk5OzjR0mgQ+TwRaK+QT2D6yKMq4ePvZQA6sXfzu4ZXniXiL/H6KUkcFSQOm1Zh5uAB8CPUhOgCngqRGhzuXB0YseqsG5ewkID8EWIcEWRTQzWqtOaLsJLYA0JQhPw3RDsbidJiNWW/XhGbZ/PAFuA5cB8ShR1wF2XvXhJpkMTtgOvkixA9lDinPfvu7wFJAZ16lX4gvrDwoh6mCijjDKINf4BnEMxY9912icAAAAASUVORK5CYII="
                  width="25"
                  height="25"
                  alt="satellite-sending-signal"
                />
              </div>
              <div>
                <h2>{t("starlinkSettingsPage.header.title")}</h2>
                <div className="starlink-subtitle">
                  {t("starlinkSettingsPage.header.subtitle")}
                </div>
              </div>
            </div>
            <div className="starlink-header-status">
              {renderBadge()}
              {data && (
                <span className="starlink-muted">
                  {t("starlinkSettingsPage.header.updatedAt", {
                    date: formatDateTime(data.timestamp),
                  })}
                </span>
              )}
            </div>
          </div>
          <div className="starlink-header-bottom">
            <div className="starlink-endpoints">
              {dish?.deviceId && <span className="starlink-chip">{dish.deviceId}</span>}
              {dish?.softwareVersion && (
                <span className="starlink-chip">{dish.softwareVersion}</span>
              )}
            </div>
            <div className="group-btn starlink-actions">
              <button className="btn btn-primary" onClick={refresh} disabled={!isConnected}>
                {t("starlinkSettingsPage.buttons.refresh")}
              </button>
              <button
                className="btn btn-secondary"
                onClick={showObstructionMap}
                disabled={!isConnected || isMapLoading}
              >
                {t("starlinkSettingsPage.buttons.obstructionMap")}
              </button>
            </div>
          </div>
          {(data?.lastError || error) && (
            <div className="starlink-box starlink-box-danger mt-20">
              <span>{t("starlinkSettingsPage.header.lastError")}</span>
              <div>{data?.lastError || error}</div>
            </div>
          )}
        </div>

        {dish && (
          <>
            <div className="starlink-grid">
              <div className="starlink-card">
                <div className="starlink-label">{t("starlinkSettingsPage.status.label")}</div>
                <h3>{t("starlinkSettingsPage.status.title")}</h3>
                <div className="starlink-stat">
                  <span>{t("starlinkSettingsPage.status.signalQuality")}</span>
                  <strong className="starlink-big">
                    {formatPercent(dish.signalQuality, 0)}
                  </strong>
                  <small>
                    {dish.isSnrAboveNoiseFloor
                      ? t("starlinkSettingsPage.status.snrAboveNoise")
                      : t("starlinkSettingsPage.status.snrBelowNoise")}
                    {dish.isSnrPersistentlyLow &&
                      ` · ${t("starlinkSettingsPage.status.snrPersistentlyLow")}`}
                  </small>
                </div>
                <div className="starlink-stats-row">
                  <div className="starlink-stat">
                    <span>{t("starlinkSettingsPage.status.uptime")}</span>
                    <strong>
                      {t("starlinkSettingsPage.common.duration", splitDuration(dish.uptimeS))}
                    </strong>
                  </div>
                  <div className="starlink-stat">
                    <span>{t("starlinkSettingsPage.status.downlink")}</span>
                    <strong>
                      {t("starlinkSettingsPage.common.mbps", {
                        value: formatMbps(dish.downlinkThroughputBps),
                      })}
                    </strong>
                  </div>
                  <div className="starlink-stat">
                    <span>{t("starlinkSettingsPage.status.uplink")}</span>
                    <strong>
                      {t("starlinkSettingsPage.common.mbps", {
                        value: formatMbps(dish.uplinkThroughputBps),
                      })}
                    </strong>
                  </div>
                  <div className="starlink-stat">
                    <span>{t("starlinkSettingsPage.status.popLatency")}</span>
                    <strong>
                      {t("starlinkSettingsPage.common.ms", {
                        value: dish.popPingLatencyMs.toFixed(0),
                      })}
                    </strong>
                  </div>
                  <div className="starlink-stat">
                    <span>{t("starlinkSettingsPage.status.dropRate")}</span>
                    <strong>{formatPercent(dish.popPingDropRate)}</strong>
                  </div>
                  <div className="starlink-stat">
                    <span>{t("starlinkSettingsPage.status.firstNonemptySlot")}</span>
                    <strong>
                      {t("starlinkSettingsPage.common.seconds", {
                        value: dish.secondsToFirstNonemptySlot.toFixed(0),
                      })}
                    </strong>
                  </div>
                </div>
                {dish.outage && (
                  <div className="starlink-box">
                    <span>{t("starlinkSettingsPage.status.outage")}</span>
                    <div>
                      {orDash(dish.outage.cause)} · {formatDateTime(dish.outage.start)} ·{" "}
                      {t("starlinkSettingsPage.common.seconds", {
                        value: dish.outage.durationS.toFixed(1),
                      })}
                      {dish.outage.didSwitch &&
                        ` · ${t("starlinkSettingsPage.status.outageSwitched")}`}
                    </div>
                  </div>
                )}
                <div className="starlink-stat">
                  <span>{t("starlinkSettingsPage.status.alerts")}</span>
                  <div className="starlink-badges">
                    {dish.alerts.length === 0 ? (
                      <span className="starlink-badge starlink-badge-success">
                        {t("starlinkSettingsPage.status.noAlerts")}
                      </span>
                    ) : (
                      dish.alerts.map((alert) => (
                        <span key={alert} className="starlink-badge starlink-badge-danger">
                          {alert}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="starlink-card">
                <div className="starlink-label">{t("starlinkSettingsPage.obstruction.label")}</div>
                <h3>{t("starlinkSettingsPage.obstruction.title")}</h3>
                <div className="starlink-stat">
                  <span>{t("starlinkSettingsPage.obstruction.current")}</span>
                  <strong className="starlink-big">
                    {dish.currentlyObstructed
                      ? t("starlinkSettingsPage.obstruction.obstructed")
                      : t("starlinkSettingsPage.obstruction.clear")}
                  </strong>
                </div>
                <div className="starlink-rows">
                  <div className="starlink-row">
                    <span>{t("starlinkSettingsPage.obstruction.fraction")}</span>
                    <strong>{formatPercent(dish.fractionObstructed, 2)}</strong>
                  </div>
                  <div className="starlink-row">
                    <span>{t("starlinkSettingsPage.obstruction.time")}</span>
                    <strong>{dish.timeObstructed.toFixed(2)}</strong>
                  </div>
                  <div className="starlink-row">
                    <span>{t("starlinkSettingsPage.obstruction.boresight")}</span>
                    <strong>
                      {degrees(dish.boresightAzimuthDeg)} / {degrees(dish.boresightElevationDeg)}
                    </strong>
                  </div>
                  <div className="starlink-row">
                    <span>{t("starlinkSettingsPage.obstruction.desiredBoresight")}</span>
                    <strong>
                      {degrees(dish.desiredBoresightAzimuthDeg)} /{" "}
                      {degrees(dish.desiredBoresightElevationDeg)}
                    </strong>
                  </div>
                  <div className="starlink-row">
                    <span>{t("starlinkSettingsPage.obstruction.tilt")}</span>
                    <strong>{degrees(dish.tiltAngleDeg)}</strong>
                  </div>
                </div>
              </div>

              <div className="starlink-card">
                <div className="starlink-label">{t("starlinkSettingsPage.gps.label")}</div>
                <h3>{t("starlinkSettingsPage.gps.title")}</h3>
                <div className="starlink-stat">
                  <span>{orDash(location?.source)}</span>
                  <strong>
                    {dish.gpsValid
                      ? t("starlinkSettingsPage.gps.valid")
                      : t("starlinkSettingsPage.gps.invalid")}
                  </strong>
                  <small>{t("starlinkSettingsPage.gps.satellites", { count: dish.gpsSats })}</small>
                </div>
                {location ? (
                  <>
                    <div className="starlink-stat">
                      <span>{t("starlinkSettingsPage.gps.position")}</span>
                      <code>
                        {location.latitude.toFixed(5)}, {location.longitude.toFixed(5)}
                      </code>
                      <code>
                        {t("starlinkSettingsPage.gps.altitude", {
                          altitude: location.altitude.toFixed(1),
                        })}
                      </code>
                      <small>
                        {t("starlinkSettingsPage.gps.accuracy", {
                          value: location.accuracyM.toFixed(1),
                        })}
                      </small>
                    </div>
                    <div className="starlink-rows">
                      <div className="starlink-row">
                        <span>{t("starlinkSettingsPage.gps.horizontalSpeed")}</span>
                        <strong>
                          {t("starlinkSettingsPage.common.mps", {
                            value: location.horizontalSpeedMps.toFixed(1),
                          })}
                        </strong>
                      </div>
                      <div className="starlink-row">
                        <span>{t("starlinkSettingsPage.gps.verticalSpeed")}</span>
                        <strong>
                          {t("starlinkSettingsPage.common.mps", {
                            value: location.verticalSpeedMps.toFixed(1),
                          })}
                        </strong>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="starlink-box">
                    <div>{t("starlinkSettingsPage.gps.locationUnavailable")}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="starlink-card">
              <div className="starlink-label">{t("starlinkSettingsPage.device.label")}</div>
              <h3>{t("starlinkSettingsPage.device.title")}</h3>
              <div className="starlink-device-grid">
                <div className="starlink-row">
                  <span>{t("starlinkSettingsPage.device.deviceId")}</span>
                  <code>{orDash(dish.deviceId)}</code>
                </div>
                <div className="starlink-row">
                  <span>{t("starlinkSettingsPage.device.hardwareVersion")}</span>
                  <strong>{orDash(dish.hardwareVersion)}</strong>
                </div>
                <div className="starlink-row">
                  <span>{t("starlinkSettingsPage.device.softwareVersion")}</span>
                  <code>{orDash(dish.softwareVersion)}</code>
                </div>
                <div className="starlink-row">
                  <span>{t("starlinkSettingsPage.device.softwareUpdateState")}</span>
                  <strong>{orDash(dish.softwareUpdateState)}</strong>
                </div>
                <div className="starlink-row">
                  <span>{t("starlinkSettingsPage.device.ethSpeed")}</span>
                  <strong>
                    {t("starlinkSettingsPage.common.mbps", { value: dish.ethSpeedMbps })}
                  </strong>
                </div>
                <div className="starlink-row">
                  <span>{t("starlinkSettingsPage.device.mobilityClass")}</span>
                  <strong>{orDash(dish.mobilityClass)}</strong>
                </div>
                <div className="starlink-row">
                  <span>{t("starlinkSettingsPage.device.classOfService")}</span>
                  <strong>{orDash(dish.classOfService)}</strong>
                </div>
                <div className="starlink-row">
                  <span>{t("starlinkSettingsPage.device.disablementCode")}</span>
                  <strong>{orDash(dish.disablementCode)}</strong>
                </div>
                <div className="starlink-row">
                  <span>{t("starlinkSettingsPage.device.stowRequested")}</span>
                  <strong>{yesNo(dish.stowRequested)}</strong>
                </div>
              </div>
            </div>
          </>
        )}

        <div className="starlink-card" ref={obstructionMapRef}>
          <div className="starlink-label">{t("starlinkSettingsPage.obstructionMap.label")}</div>
          <div className="starlink-card-header">
            <h3>{t("starlinkSettingsPage.obstructionMap.title")}</h3>
            <button
              className="btn btn-secondary"
              onClick={loadObstructionMap}
              disabled={!isConnected || isMapLoading}
            >
              {t("starlinkSettingsPage.obstructionMap.refresh")}
            </button>
          </div>
          {obstructionMap ? (
            <div className="starlink-map">
              <StarlinkObstructionMap map={obstructionMap} />
              <div className="starlink-map-info">
                <div className="starlink-legend">
                  <span>{t("starlinkSettingsPage.obstructionMap.obstructed")}</span>
                  <div className="starlink-legend-bar" />
                  <span>{t("starlinkSettingsPage.obstructionMap.clear")}</span>
                </div>
                <p className="starlink-map-help">
                  {t("starlinkSettingsPage.obstructionMap.help")}
                </p>
                <small>
                  {t("starlinkSettingsPage.obstructionMap.updatedAt", {
                    date: formatDateTime(obstructionMap.timestamp),
                  })}
                </small>
                <small>
                  {t("starlinkSettingsPage.obstructionMap.minElevation", {
                    value: obstructionMap.minElevationDeg.toFixed(0),
                  })}
                </small>
                {obstructionMap.referenceFrame && (
                  <small>
                    {t("starlinkSettingsPage.obstructionMap.referenceFrame", {
                      value: obstructionMap.referenceFrame,
                    })}
                  </small>
                )}
              </div>
            </div>
          ) : (
            <div className="starlink-muted">
              {isMapLoading
                ? t("starlinkSettingsPage.obstructionMap.loading")
                : t("starlinkSettingsPage.obstructionMap.empty")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StarlinkSettingsPage;
